/**
 * Zustand store for Copilot Agent - Enterprise 3-panel UI
 *
 * Manages:
 * - Chat messages (user + assistant only - no tool messages)
 * - Workspace tabs (query result artifacts with structured data)
 * - Schema tree (connector → table → column)
 * - SQL approval state
 * - Streaming state with action labels, tool-call count, and liveness timestamps
 */

import { create } from 'zustand';

// ============================================================================
// Types
// ============================================================================

export interface KnowledgePillItem {
  name: string;
  node_type: string;
  definition?: string;
  sql?: string;
}

export interface FindingPillItem {
  id: string;
  title: string;
  narrative: string;
  dataPoint?: Record<string, any>;
}

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  auditId?: string;
  type?: 'knowledge_captured' | 'finding';
  knowledgeItems?: KnowledgePillItem[];
  findingItem?: FindingPillItem;
}

/** Ordered stream for mobile unified chat (matches SSE / history event order). */
export type CopilotTimelineEntry =
  | { type: 'user'; messageId: string }
  | { type: 'assistant'; messageId: string }
  | { type: 'streaming_assistant' }
  | { type: 'artifact'; artifactId: string }
  | { type: 'sql_approval'; id: string };

export interface AuditDataSource {
  frameId?: string;
  title: string;
  connectorId: string;
  connectorName: string;
  connectorType: string;
  rowCount: number;
  columns: string[];
  sql?: string;
  timestamp: string;
}

export interface AuditStep {
  type: string;
  toolName?: string;
  toolArgs?: Record<string, any>;
  summary?: string;
  timestamp: string;
}

export interface AuditChart {
  title: string;
  chartType: string;
  rowCount: number;
  columns: string[];
  timestamp: string;
}

export interface CalculationAuditReport {
  id: string;
  threadId: string | null;
  question: string;
  startedAt: string;
  completedAt?: string;
  /** When false, hide “data trail” UI for prose-only / clarification turns. */
  showAuditLink?: boolean;
  dataSources: AuditDataSource[];
  steps: AuditStep[];
  charts: AuditChart[];
  story?: {
    headline?: string;
    dataSources?: {
      summary?: string;
      connectors?: string[];
      tablesOrEndpoints?: string[];
      details?: Array<{
        name?: string;
        connectorType?: string;
        records?: number;
        columns?: number;
        frameId?: string;
        query?: string;
        requestType?: string;
        tablesOrEndpoints?: string[];
      }>;
    };
    staging?: {
      summary?: string;
      stagedRecords?: number;
      frameCount?: number;
    };
    calculation?: {
      summary?: string;
      sqlStepCount?: number;
      finalSql?: string;
      steps?: Array<{
        tool?: string;
        sql?: string;
      }>;
    };
    outputs?: {
      summary?: string;
      resultTypes?: string[];
      chartCount?: number;
      chartTitles?: string[];
      chartDetails?: Array<{
        title?: string;
        type?: string;
        records?: number;
        columns?: string[];
        xFields?: string[];
        yFields?: string[];
        timeUnit?: string;
      }>;
      forecastCount?: number;
      forecastDetails?: Array<{
        title?: string;
        frameId?: string;
        selectedModel?: string;
        horizon?: number;
        timeUnit?: string;
        confidenceLevel?: number;
        confidenceLevelPct?: number;
        reliability?: string;
        historyPoints?: number;
        targetColumn?: string;
        timeColumn?: string;
        periodsCount?: number;
        firstPeriod?: { period?: string; prediction?: number; lower?: number; upper?: number };
        candidateModels?: Array<{
          model?: string;
          mae?: number | null;
          rmse?: number | null;
          wape?: number | null;
          p95AbsError?: number | null;
          selectionScore?: number | null;
        }>;
        selectedModelMetrics?: {
          mae?: number | null;
          rmse?: number | null;
          wape?: number | null;
          p95AbsError?: number | null;
          selectionScore?: number | null;
        };
        caveats?: string[];
        dataWindowStart?: string;
        dataWindowEnd?: string;
        droppedPoints?: number;
        rawRows?: number;
        preparedPoints?: number;
        seasonLength?: number;
      }>;
    };
  };
}

/** Whether to show the per-message “calculation trail” control (matches backend when present). */
export function copilotAuditHasDataTrail(report: CalculationAuditReport | null | undefined): boolean {
  if (!report) return false;
  if (report.showAuditLink === false) return false;
  if (report.showAuditLink === true) return true;
  if ((report.charts?.length ?? 0) > 0) return true;
  if ((report.dataSources?.length ?? 0) > 0) return true;
  if ((report.story?.calculation?.steps?.length ?? 0) > 0) return true;
  if ((report.story?.outputs?.forecastDetails?.length ?? 0) > 0) return true;
  for (const st of report.steps ?? []) {
    if (st.type === 'forecast_summary') return true;
    if (st.type === 'tool_result' && st.toolName === 'propose_metric_card') return true;
    if (
      st.type === 'tool_call'
      && (st.toolName === 'analyze_data' || st.toolName === 'execute_sql')
    ) {
      return true;
    }
  }
  return false;
}

export interface SQLApproval {
  sql: string;
  title: string;
  connectorId: string;
  connectorName: string;
  connectorType: string;
  connectorCategory?: string;
  sessionId?: string;
  isFileConnector?: boolean;
  isApiConnector?: boolean;
  entityId?: string;
  entityName?: string;
  columns?: string[];
  resource?: string;
  properties?: string[];
  limit?: number | null;
  /** Correlates with `timeline` sql_approval entry (live stream only). */
  timelineId?: string;
}

/** Structured query result for the workspace DataGrid */
export interface QueryResultTab {
  id: string;
  type: 'query_result';
  title: string;
  columns: string[];
  rows: any[][];
  rowCount: number;
  elapsedMs: number;
  sql: string;
  connectorId: string;
  connectorName: string;
  connectorType: string;
  /** Backend category — 'sql', 'api', 'rest', 'files', 'local', etc. Drives label/copy. */
  connectorCategory?: string;
  isFileConnector: boolean;
  truncated: boolean;
  createdAt: string;
}

/** Chart artifact - ECharts spec with embedded data */
export interface ChartArtifact {
  id: string;
  type: 'chart';
  title: string;
  chartType: string;   // echarts sub-type: bar, line, pie, etc.
  spec: Record<string, any>;  // full ECharts option
  rowCount: number;
  columns: string[];
  chartIntent: Record<string, any>;
  createdAt: string;
  eventId?: string;    // backend DB event_id - used for PDF export matching
}

/** Finding artifact - pinned insight from save_finding tool, shown in workspace panel */
export interface FindingArtifact {
  id: string;
  type: 'finding';
  title: string;
  narrative: string;
  dataPoint: Record<string, any>;
  artifactRefs: string[];
  createdAt: string;
}

/** Metric card artifact - single KPI value from propose_metric_card tool */
export interface MetricArtifact {
  id: string;
  type: 'metric_card';
  title: string;
  metricName: string;
  metricValue: any;
  columns: string[];
  rowCount: number;
  createdAt: string;
  eventId?: string;
}

/** Discriminated union - all workspace artifact types */
export type WorkspaceArtifact = QueryResultTab | ChartArtifact | FindingArtifact | MetricArtifact;

/** Pinned finding / insight from save_finding tool */
export interface Finding {
  id: string;
  title: string;
  narrative: string;
  dataPoint: Record<string, any>;
  artifactRefs: string[];
  createdAt: string;
}

/** Schema tree node for the Data Explorer */
export interface SchemaColumn {
  name: string;
  type: string;
  nullPct?: number | null;
  isPii?: boolean;
  piiLevel?: string;
  description?: string;
}

export interface SchemaTable {
  name: string;
  columns: SchemaColumn[];
  rowCount?: number | null;
}

export interface SchemaConnector {
  connectorId: string;
  name: string;
  type: string;
  tables: SchemaTable[];
}

// ============================================================================
// Store
// ============================================================================

/** Thread summary for the history list */
export interface CopilotThread {
  threadId: string;
  title: string | null;
  agentType: string;
  createdAt?: string | null;
}

interface CopilotState {
  // Thread
  threadId: string | null;
  setThreadId: (id: string | null) => void;
  threadTitle: string | null;
  setThreadTitle: (title: string | null) => void;

  // Thread history list
  threadList: CopilotThread[];
  setThreadList: (threads: CopilotThread[]) => void;

  // History loading state
  isLoadingHistory: boolean;
  setIsLoadingHistory: (loading: boolean) => void;

  // History replay
  loadHistoryMessages: (
    messages: CopilotMessage[],
    artifacts: WorkspaceArtifact[],
    findings?: Finding[],
    timeline?: CopilotTimelineEntry[],
  ) => void;

  // Mobile unified timeline (ordered: user → … → artifacts → assistant → sql approval …)
  timeline: CopilotTimelineEntry[];
  appendTimelineUser: (messageId: string) => void;
  appendTimelineArtifact: (artifactId: string) => void;
  ensureTimelineStreamingAssistant: () => void;
  finalizeTimelineAssistant: (messageId: string) => void;
  /** Remove trailing streaming slot when flush produced no assistant message */
  popTimelineStreamingIfStuck: () => void;
  appendTimelineSqlApproval: (id: string) => void;
  appendTimelineAssistantMessage: (messageId: string) => void;
  clearTimeline: () => void;

  // Messages (user + assistant only)
  messages: CopilotMessage[];
  addMessage: (msg: CopilotMessage) => void;
  clearMessages: () => void;

  // Explainability audit (backend-native)
  auditReports: Record<string, CalculationAuditReport>;
  addAuditReport: (report: CalculationAuditReport, reportId?: string) => string;
  pendingAuditId: string | null;
  setPendingAuditId: (auditId: string | null) => void;

  // Streaming
  isStreaming: boolean;
  setIsStreaming: (streaming: boolean) => void;
  streamingContent: string;
  setStreamingContent: (content: string) => void;
  appendStreamingContent: (chunk: string) => void;
  streamingAction: string;
  setStreamingAction: (action: string) => void;
  /** Count of tool_call events this stream (for “Working…” / idle copy only; not shown as a list) */
  streamingToolCallCount: number;
  noteStreamingToolCall: () => void;
  /** Wall-clock ms when the current stream started (for elapsed timer) */
  streamingStartedAt: number | null;
  /** Updated on every SSE event while streaming (for idle / escalation copy) */
  streamingLastEventAt: number | null;
  touchStreamingActivity: () => void;

  // SQL Approval
  pendingSQLApproval: SQLApproval | null;
  setPendingSQLApproval: (approval: SQLApproval | null) => void;

  // Workspace artifacts (query results + charts)
  workspaceTabs: WorkspaceArtifact[];
  activeTabId: string | null;
  addWorkspaceTab: (tab: WorkspaceArtifact) => void;
  removeWorkspaceTab: (tabId: string) => void;
  setActiveTab: (tabId: string | null) => void;

  // Artifact visibility tracking (unread dot + entrance animation)
  seenArtifactIds: Set<string>;
  markArtifactSeen: (id: string) => void;

  // Schema tree (Data Explorer)
  schemaTree: SchemaConnector[];
  setSchemaTree: (tree: SchemaConnector[]) => void;

  // Data Explorer collapsed state
  explorerCollapsed: boolean;
  setExplorerCollapsed: (collapsed: boolean) => void;

  // Pinned findings (from save_finding tool)
  findings: Finding[];
  addFinding: (finding: Finding) => void;

  // Context usage (from backend context_usage SSE event)
  contextUsage: { used: number; budget: number; pct: number; trimmed: boolean; trimmedCount: number } | null;
  setContextUsage: (usage: { used: number; budget: number; pct: number; trimmed: boolean; trimmedCount: number } | null) => void;

  // Input
  inputText: string;
  setInputText: (text: string) => void;
  pendingAutoSend: boolean;
  setPendingAutoSend: (v: boolean) => void;

  // Reset
  resetSession: () => void;
}

export const useCopilotStore = create<CopilotState>()((set) => ({
  // Thread
  threadId: null,
  setThreadId: (id) => set({ threadId: id }),
  threadTitle: null,
  setThreadTitle: (title) => set({ threadTitle: title }),

  // Thread history list
  threadList: [],
  setThreadList: (threads) => set({ threadList: threads }),

  isLoadingHistory: false,
  setIsLoadingHistory: (loading) => set({ isLoadingHistory: loading }),

  // History replay - bulk-load messages and artifacts from the history API
  loadHistoryMessages: (messages, artifacts, findings, timeline) =>
    set({
      messages,
      workspaceTabs: artifacts,
      activeTabId: artifacts.length > 0 ? artifacts[artifacts.length - 1].id : null,
      findings: findings || [],
      auditReports: {},
      pendingAuditId: null,
      timeline: timeline ?? [],
      // Mark all history artifacts as seen (no entrance animation for loaded history)
      seenArtifactIds: new Set(artifacts.map((a: WorkspaceArtifact) => a.id)),
    }),

  timeline: [],
  appendTimelineUser: (messageId) =>
    set((s) => ({ timeline: [...s.timeline, { type: 'user', messageId }] })),
  appendTimelineArtifact: (artifactId) =>
    set((s) => {
      if (s.timeline.some((e) => e.type === 'artifact' && e.artifactId === artifactId)) return {};
      return { timeline: [...s.timeline, { type: 'artifact', artifactId }] };
    }),
  ensureTimelineStreamingAssistant: () =>
    set((s) => {
      const last = s.timeline[s.timeline.length - 1];
      if (last?.type === 'streaming_assistant') return {};
      return { timeline: [...s.timeline, { type: 'streaming_assistant' }] };
    }),
  finalizeTimelineAssistant: (messageId) =>
    set((s) => {
      const t = [...s.timeline];
      const last = t[t.length - 1];
      if (last?.type === 'streaming_assistant') {
        t[t.length - 1] = { type: 'assistant', messageId };
      } else {
        t.push({ type: 'assistant', messageId });
      }
      return { timeline: t };
    }),
  popTimelineStreamingIfStuck: () =>
    set((s) => {
      const last = s.timeline[s.timeline.length - 1];
      if (last?.type === 'streaming_assistant') {
        return { timeline: s.timeline.slice(0, -1) };
      }
      return {};
    }),
  appendTimelineSqlApproval: (id) =>
    set((s) => ({ timeline: [...s.timeline, { type: 'sql_approval', id }] })),
  appendTimelineAssistantMessage: (messageId) =>
    set((s) => ({ timeline: [...s.timeline, { type: 'assistant', messageId }] })),
  clearTimeline: () => set({ timeline: [] }),

  // Messages
  messages: [],
  addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  clearMessages: () => set({ messages: [] }),

  // Explainability audit (backend-native only)
  auditReports: {},
  addAuditReport: (report, reportId) => {
    const id = reportId || report.id || `audit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const normalized: CalculationAuditReport = {
      ...report,
      id,
    };
    set((prev) => ({
      auditReports: {
        ...prev.auditReports,
        [id]: normalized,
      },
    }));
    return id;
  },
  pendingAuditId: null,
  setPendingAuditId: (auditId) => set({ pendingAuditId: auditId }),

  // Streaming
  isStreaming: false,
  setIsStreaming: (streaming) =>
    set(() => ({
      isStreaming: streaming,
      ...(streaming
        ? {
            streamingStartedAt: Date.now(),
            streamingLastEventAt: Date.now(),
            streamingToolCallCount: 0,
          }
        : {
            streamingAction: '',
            streamingStartedAt: null,
            streamingLastEventAt: null,
            streamingToolCallCount: 0,
          }),
    })),
  streamingContent: '',
  setStreamingContent: (content) => set({ streamingContent: content }),
  appendStreamingContent: (chunk) =>
    set((s) => ({ streamingContent: s.streamingContent + chunk })),
  streamingAction: '',
  setStreamingAction: (action: string) => set({ streamingAction: action }),
  streamingToolCallCount: 0,
  noteStreamingToolCall: () =>
    set((s) => ({ streamingToolCallCount: s.streamingToolCallCount + 1 })),
  streamingStartedAt: null,
  streamingLastEventAt: null,
  touchStreamingActivity: () => set({ streamingLastEventAt: Date.now() }),

  // SQL Approval
  pendingSQLApproval: null,
  setPendingSQLApproval: (approval) => set({ pendingSQLApproval: approval }),

  // Workspace artifacts
  workspaceTabs: [],
  activeTabId: null,
  addWorkspaceTab: (tab: WorkspaceArtifact) =>
    set((s: CopilotState) => {
      const exists = s.workspaceTabs.some((t: WorkspaceArtifact) => t.id === tab.id);
      return {
        workspaceTabs: exists
          ? s.workspaceTabs.map((t: WorkspaceArtifact) => t.id === tab.id ? tab : t)
          : [...s.workspaceTabs, tab],
        activeTabId: tab.id,
        // Don't mark new artifacts as seen - they'll get the entrance animation + unread dot
      };
    }),
  removeWorkspaceTab: (tabId: string) =>
    set((s: CopilotState) => {
      const filtered = s.workspaceTabs.filter((t: WorkspaceArtifact) => t.id !== tabId);
      return {
        workspaceTabs: filtered,
        activeTabId:
          s.activeTabId === tabId
            ? filtered.length > 0
              ? filtered[filtered.length - 1].id
              : null
            : s.activeTabId,
      };
    }),
  setActiveTab: (tabId: string | null) =>
    set((s: CopilotState) => {
      const next: Partial<CopilotState> = { activeTabId: tabId };
      // Mark the artifact as seen when user selects it
      if (tabId && !s.seenArtifactIds.has(tabId)) {
        const updated = new Set(s.seenArtifactIds);
        updated.add(tabId);
        next.seenArtifactIds = updated;
      }
      return next;
    }),

  // Artifact visibility tracking
  seenArtifactIds: new Set<string>(),
  markArtifactSeen: (id: string) =>
    set((s: CopilotState) => {
      if (s.seenArtifactIds.has(id)) return {};
      const updated = new Set(s.seenArtifactIds);
      updated.add(id);
      return { seenArtifactIds: updated };
    }),

  // Schema tree
  schemaTree: [],
  setSchemaTree: (tree: SchemaConnector[]) => set({ schemaTree: tree }),

  // Data Explorer
  explorerCollapsed: false,
  setExplorerCollapsed: (collapsed: boolean) => set({ explorerCollapsed: collapsed }),

  // Pinned findings
  findings: [],
  addFinding: (finding: Finding) =>
    set((s: CopilotState) => ({ findings: [...s.findings, finding] })),

  // Context usage
  contextUsage: null,
  setContextUsage: (usage) => set({ contextUsage: usage }),

  // Input
  inputText: '',
  setInputText: (text) => set({ inputText: text }),
  pendingAutoSend: false,
  setPendingAutoSend: (v) => set({ pendingAutoSend: v }),

  // Reset
  resetSession: () =>
    set({
      messages: [],
      workspaceTabs: [],
      activeTabId: null,
      timeline: [],
      isStreaming: false,
      streamingContent: '',
      streamingAction: '',
      streamingToolCallCount: 0,
      streamingStartedAt: null,
      streamingLastEventAt: null,
      pendingSQLApproval: null,
      inputText: '',
      pendingAutoSend: false,
      threadTitle: null,
      contextUsage: null,
      findings: [],
      seenArtifactIds: new Set<string>(),
      auditReports: {},
      pendingAuditId: null,
    }),
}));
